import { FC, ReactNode, CSSProperties } from 'react'

import TitleWithEducationTip from 'core/components/lib/TitleWithEducationTip'

import { FormContextProvider } from 'loan/components/actionCards/shared/DisabledContext'

import InfoCardBottomCap from './InfoCardBottomCap'
import InfoCardContainer from './InfoCardContainer'

type InfoCardProps = {
  children?: ReactNode
  title?: ReactNode
  subTitle?: string
  customClassName?: string
  borderless?: boolean
  style?: CSSProperties
  /** If true, the card is styled for agent-only view */
  agent?: boolean
  /** If included, there will be a help tooltip next
   * to the title with this text in it. If no title is
   * provided, there is no help tooltip.
   */
  ellipsizeTitle?: boolean
  helpText?: ReactNode
  helpTextMargin?: string
  removeHelpTextTitle?: boolean
  iconMargin?: string
  /** The parent component can pass the loading status stored
   * in redux for the api call here to show a message at the
   * bottom of the InfoCard.
   */
  status?: 'success' | 'loading' | 'error'
  /** The message that should be displayed at the bottom of
   * the InfoCard when `status` is set to "success".
   */
  successMessage?: ReactNode
  errorMessage?: ReactNode
  completeUrl?: string
  onComplete?: () => void
  statusIcon?: ReactNode
  disabled?: boolean
  helpTitle?: string
  className?: string
}

const InfoCard: FC<InfoCardProps> = (props) => {
  const {
    agent,
    borderless,
    children,
    completeUrl,
    customClassName,
    disabled,
    ellipsizeTitle,
    errorMessage,
    helpText,
    helpTextMargin,
    helpTitle,
    iconMargin,
    onComplete,
    removeHelpTextTitle,
    status,
    statusIcon,
    style,
    subTitle,
    successMessage,
    title,
    className,
  } = props

  const bottomCap = status && (
    <InfoCardBottomCap
      completeUrl={completeUrl}
      errorMessage={errorMessage}
      onComplete={onComplete}
      status={status}
      successMessage={successMessage ?? 'Action complete'}
    />
  )

  return (
    <FormContextProvider disabled={disabled}>
      <InfoCardContainer
        agent={agent}
        borderless={borderless}
        bottomCap={bottomCap}
        className={className}
        customClassName={customClassName}
        style={style}
      >
        <TitleWithEducationTip
          ellipsize={ellipsizeTitle}
          helpText={helpText}
          helpTextMargin={helpTextMargin}
          helpTitle={helpTitle ?? 'Agent Education:'}
          iconMargin={iconMargin}
          removeHelpTextTitle={removeHelpTextTitle}
          statusIcon={statusIcon}
          subTitle={subTitle}
          title={title}
        />

        {children}
      </InfoCardContainer>
    </FormContextProvider>
  )
}

export default InfoCard
